import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import API_URL from '../../config'
import { setAuthToken } from '../../services/auth'

// Status options for filtering
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active/Complete' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'error', label: 'Error' },
  { value: 'skipped', label: 'Skipped' },
]

// Media type options for filtering
const MEDIA_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'song', label: 'Songs' },
  { value: 'album', label: 'Albums' },
  { value: 'playlist', label: 'Playlists' },
  { value: 'mix', label: 'Mixes' },
]

// Service options for filtering
const SERVICE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'apple_music', label: 'Apple Music' },
  { value: 'bandcamp', label: 'Bandcamp' },
  { value: 'youtube', label: 'YouTube' },
]

const ReconciliationSearch = () => {
  // State for search criteria
  const [searchQuery, setSearchQuery] = useState('')
  const [advancedVisible, setAdvancedVisible] = useState(false)

  // Advanced search criteria
  const [title, setTitle] = useState('')
  const [artist, setArtist] = useState('')
  const [mediaType, setMediaType] = useState('')
  const [status, setStatus] = useState('')
  const [service, setService] = useState('')
  const [cloudId, setCloudId] = useState('')
  const [hasCloud, setHasCloud] = useState('')

  // State for records
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)

  // Function to handle search
  const handleSearch = async (e) => {
    if (e) e.preventDefault()

    try {
      setLoading(true)
      setError(null)

      // Set auth token for the request
      setAuthToken(localStorage.getItem('token'))

      // Build params
      const params = {
        page,
        pageSize,
      }

      // Add search criteria if provided
      if (searchQuery) params.query = searchQuery
      if (title) params.title = title
      if (artist) params.artist = artist
      if (mediaType) params.mediaType = mediaType
      if (status) params.status = status
      if (service) params.service = service
      if (cloudId) params.cloudId = cloudId
      if (hasCloud) params.hasCloud = hasCloud

      // Make API request
      const response = await axios.get(`${API_URL}admin/reconciliation/search/`, { params })

      // Update state with results
      setRecords(response.data.results)
      setTotalPages(response.data.pagination.totalPages)
      setTotalResults(response.data.pagination.totalResults)
    } catch (err) {
      console.error('Error searching records:', err)
      setError('Failed to search records. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  // Function to handle pagination
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
      handleSearch()
    }
  }

  // Function to reset search criteria
  const resetSearch = () => {
    setSearchQuery('')
    setTitle('')
    setArtist('')
    setMediaType('')
    setStatus('')
    setService('')
    setCloudId('')
    setHasCloud('')
    setPage(1)
  }

  // Function to render status icon
  const renderStatusIcon = (status) => {
    if (status === 'active' || status === 'complete') {
      return <span className='text-green-500'>✓</span>
    } else if (status === 'pending' || status === 'processing') {
      return <span className='text-yellow-500'>⟳</span>
    } else {
      return <span className='text-red-500'>✗</span>
    }
  }

  // Format date for display
  const formatDate = (isoDate) => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    return date.toLocaleString()
  }

  return (
    <div className='p-4'>
      <h1 className='mb-6 text-2xl font-bold'>Reconciliation Search</h1>

      {/* Navigation */}
      <div className='mb-6 flex border-b pb-2'>
        <Link to='/admin/reconciliation' className='px-4 py-2'>
          Dashboard
        </Link>
        <Link to='/admin/reconciliation/recent' className='px-4 py-2'>
          Recent
        </Link>
        <Link to='/admin/reconciliation/search' className='px-4 py-2 font-bold'>
          Search
        </Link>
        <Link to='/admin/reconciliation/manual' className='px-4 py-2'>
          Manual
        </Link>
      </div>

      {/* Search Form */}
      <div className='mb-6 rounded border p-4'>
        <form onSubmit={handleSearch}>
          <div className='mb-4 flex items-center'>
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search for titles, artists, etc.'
              className='mr-3 grow border p-2'
            />
            <button
              type='submit'
              className='bg-blue-500 text-white hover:bg-blue-600 rounded px-4 py-2'
            >
              Search
            </button>
            <button
              type='button'
              className='text-blue-500 ml-2 hover:underline'
              onClick={() => setAdvancedVisible(!advancedVisible)}
            >
              {advancedVisible ? 'Hide Advanced' : 'Advanced Options'}
            </button>
          </div>

          {/* Advanced search options */}
          {advancedVisible && (
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3'>
              <div>
                <label className='mb-1 block'>Title:</label>
                <input
                  type='text'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className='w-full border p-2'
                />
              </div>

              <div>
                <label className='mb-1 block'>Artist:</label>
                <input
                  type='text'
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  className='w-full border p-2'
                />
              </div>

              <div>
                <label className='mb-1 block'>Media Type:</label>
                <select
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  className='w-full border p-2'
                >
                  {MEDIA_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='mb-1 block'>Status:</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className='w-full border p-2'
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='mb-1 block'>Service:</label>
                <select
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className='w-full border p-2'
                >
                  {SERVICE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='mb-1 block'>Cloud ID:</label>
                <input
                  type='text'
                  value={cloudId}
                  onChange={(e) => setCloudId(e.target.value)}
                  className='w-full border p-2'
                />
              </div>

              <div>
                <label className='mb-1 block'>Has Cloud:</label>
                <select
                  value={hasCloud}
                  onChange={(e) => setHasCloud(e.target.value)}
                  className='w-full border p-2'
                >
                  <option value=''>Any</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </select>
              </div>

              <div className='flex items-end'>
                <button
                  type='button'
                  onClick={resetSearch}
                  className='bg-gray-200 hover:bg-gray-300 rounded px-4 py-2'
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Error Message */}
      {error && <div className='bg-red-100 text-red-700 mb-6 rounded p-4'>{error}</div>}

      {/* Results Table */}
      <div className='mb-6 overflow-x-auto'>
        {loading ? (
          <div className='p-4 text-center'>Searching records...</div>
        ) : records.length > 0 ? (
          <>
            <div className='mb-2'>Found {totalResults} results</div>
            <table className='min-w-full border'>
              <thead>
                <tr className='bg-gray-100'>
                  <th className='border px-4 py-2'>Status</th>
                  <th className='border px-4 py-2'>Type</th>
                  <th className='border px-4 py-2'>Title</th>
                  <th className='border px-4 py-2'>Artists</th>
                  <th className='border px-4 py-2'>Service</th>
                  <th className='border px-4 py-2'>Processed</th>
                  <th className='border px-4 py-2'>Cloud ID</th>
                  <th className='border px-4 py-2'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr
                    key={`${record.id}-${record.instanceId}-${record.service}`}
                    className='hover:bg-gray-50'
                  >
                    <td className='border px-4 py-2 text-center'>
                      {renderStatusIcon(record.status)}
                    </td>
                    <td className='border px-4 py-2 capitalize'>{record.mediaType || 'Unknown'}</td>
                    <td className='border px-4 py-2'>{record.title}</td>
                    <td className='border px-4 py-2'>{record.artists.join(', ')}</td>
                    <td className='border px-4 py-2'>{record.service || 'Unknown'}</td>
                    <td className='border px-4 py-2'>{formatDate(record.processingTime)}</td>
                    <td className='border px-4 py-2'>{record.cloudId || '-'}</td>
                    <td className='border px-4 py-2'>
                      <Link
                        to={`/admin/reconciliation/record/${record.id}`}
                        className='bg-blue-500 text-white hover:bg-blue-600 rounded px-2 py-1'
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : searchQuery ||
          title ||
          artist ||
          mediaType ||
          status ||
          service ||
          cloudId ||
          hasCloud ? (
          <div className='p-4 text-center'>No records found matching your search criteria</div>
        ) : (
          <div className='p-4 text-center'>
            Enter search criteria to find reconciliation records
          </div>
        )}
      </div>

      {/* Pagination */}
      {records.length > 0 && (
        <div className='flex items-center justify-between'>
          <div>
            Showing {records.length} of {totalResults} results
          </div>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className={`rounded px-3 py-1 ${page === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              &lt; Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
              className={`rounded px-3 py-1 ${page === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReconciliationSearch
